// src/components/PrivacyPolicy.jsx
import React from "react";

const COMPANY_NAME = "Lian Foong";
const APP_NAME = "Lian Foong App"; // change if your app name is different
const WEBSITE_URL = "https://admin.lianfoong.com";
const PRIVACY_EMAIL = "support@lianfoong.com";
const LAST_UPDATED = "10 Dec 2025";

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.meta}>
          <strong>{COMPANY_NAME}</strong> • Last updated: {LAST_UPDATED}
        </p>

        <p style={styles.p}>
          This Privacy Policy explains how <strong>{COMPANY_NAME}</strong> (“we”,
          “us”, “our”) collects, uses, shares, and protects information when you
          use <strong>{APP_NAME}</strong> and related services (the “Service”).
        </p>

        <p style={styles.p}>
          By using the Service, you agree to the practices described in this
          Privacy Policy. If you do not agree, please do not use the Service.
        </p>

        <hr style={styles.hr} />

        <h2 style={styles.h2}>1) Information we collect</h2>

        <h3 style={styles.h3}>A. Information you provide</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Account information</strong> (e.g., name, email address,
            phone number) when you register, sign in, or manage your profile.
          </li>
          <li style={styles.li}>
            <strong>Support and communications</strong> (e.g., messages you send
            us, feedback, bug reports) and any information you choose to include.
          </li>
          <li style={styles.li}>
            <strong>Business/transaction inputs</strong> that you enter into the
            Service as part of normal usage (for example, forms, records, or
            operational data used to perform the Service’s functions).
          </li>
        </ul>

        <h3 style={styles.h3}>B. Information collected automatically</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Device information</strong> (e.g., device model, operating
            system, language settings, unique device identifiers where available).
          </li>
          <li style={styles.li}>
            <strong>App and usage data</strong> (e.g., features used, screens
            viewed, timestamps, performance metrics).
          </li>
          <li style={styles.li}>
            <strong>Diagnostics</strong> (e.g., crash logs, error logs) to improve
            reliability and fix issues.
          </li>
        </ul>

        <p style={styles.note}>
          We only collect information that is reasonably necessary to operate,
          maintain, and improve the Service.
        </p>

        <h2 style={styles.h2}>2) How we use your information</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Provide the Service</strong> (e.g., authentication, access to
            features, processing your requests).
          </li>
          <li style={styles.li}>
            <strong>Operate and maintain</strong> the Service (e.g., monitoring,
            debugging, improving performance and stability).
          </li>
          <li style={styles.li}>
            <strong>Security and fraud prevention</strong> (e.g., protecting
            accounts, preventing unauthorized access).
          </li>
          <li style={styles.li}>
            <strong>Support and communications</strong> (e.g., responding to
            inquiries, sending service-related notices).
          </li>
          <li style={styles.li}>
            <strong>Compliance</strong> with legal obligations and enforcement of
            our terms and policies.
          </li>
        </ul>

        <h2 style={styles.h2}>3) Sharing and disclosure</h2>
        <p style={styles.p}>
          We do not sell your personal information. We may share information only
          in the following situations:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Service providers</strong> that help us run the Service (for
            example, hosting, database, analytics, crash reporting, customer
            support tools). These providers are permitted to process information
            only on our instructions and for the purpose of providing services to
            us.
          </li>
          <li style={styles.li}>
            <strong>Legal requirements</strong> if required by law, regulation,
            legal process, or enforceable government request.
          </li>
          <li style={styles.li}>
            <strong>Security and protection</strong> to protect the rights,
            property, or safety of {COMPANY_NAME}, our users, or others, including
            enforcing our terms or investigating potential fraud or security
            issues.
          </li>
          <li style={styles.li}>
            <strong>Business transfers</strong> if we are involved in a merger,
            acquisition, financing, reorganization, or sale of assets (in which
            case information may be transferred as part of that transaction).
          </li>
        </ul>

        <h2 style={styles.h2}>4) Data retention</h2>
        <p style={styles.p}>
          We keep information for as long as reasonably necessary to provide the
          Service and for legitimate business purposes (such as maintaining
          records, resolving disputes, enforcing agreements, and complying with
          legal obligations). When information is no longer needed, we take
          reasonable steps to delete or anonymize it.
        </p>

        <h2 style={styles.h2}>5) Security</h2>
        <p style={styles.p}>
          We use reasonable administrative, technical, and organizational measures
          designed to protect information from unauthorized access, loss, misuse,
          or alteration. However, no system is 100% secure, and we cannot
          guarantee absolute security.
        </p>

        <h2 style={styles.h2}>6) Your choices and rights</h2>
        <p style={styles.p}>
          Depending on your location and applicable laws, you may have rights to:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>Request access to your personal information</li>
          <li style={styles.li}>Request correction of inaccurate information</li>
          <li style={styles.li}>Request deletion of your personal information</li>
          <li style={styles.li}>
            Withdraw consent where processing is based on consent
          </li>
        </ul>
        <p style={styles.p}>
          To make a request, contact us at{" "}
          <a style={styles.link} href={`mailto:${PRIVACY_EMAIL}`}>
            {PRIVACY_EMAIL}
          </a>
          . We may need to verify your identity before responding.
        </p>

        <h2 style={styles.h2}>7) Children’s privacy</h2>
        <p style={styles.p}>
          The Service is not intended for children under 13, and we do not
          knowingly collect personal information from children under 13. If you
          believe a child has provided personal information, please contact us so
          we can take appropriate steps.
        </p>

        <h2 style={styles.h2}>8) International transfers</h2>
        <p style={styles.p}>
          Your information may be processed in locations where our service
          providers operate. We take reasonable steps to ensure appropriate
          safeguards are in place when information is transferred.
        </p>

        <h2 style={styles.h2}>9) Changes to this Privacy Policy</h2>
        <p style={styles.p}>
          We may update this Privacy Policy from time to time. We will post the
          updated version on this page and update the “Last updated” date above.
          Your continued use of the Service after changes take effect means you
          accept the updated policy.
        </p>

        <h2 style={styles.h2}>10) Contact us</h2>
        <p style={styles.p}>
          If you have questions about this Privacy Policy, contact us:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Email:{" "}
            <a style={styles.link} href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>
          </li>
        </ul>

        <div style={styles.footer}>
          <small style={styles.small}>
            © {new Date().getFullYear()} {COMPANY_NAME}
          </small>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "1500px 16px 32px", // ✅ more top padding
    background: "#bfeef5",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    boxSizing: "border-box",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    color: "#0f172a",
  },
  card: {
    width: "100%",
    maxWidth: 920,
    background: "#ffffff",
    borderRadius: 18,
    padding: 28,
    boxShadow: "0 12px 30px rgba(0,0,0,0.10)",
    border: "1px solid rgba(15, 23, 42, 0.08)",
  },
  h1: {
    margin: 0,
    fontSize: 32,
    letterSpacing: "-0.02em",
  },
  meta: {
    marginTop: 8,
    marginBottom: 0,
    color: "#475569",
  },
  hr: {
    margin: "18px 0",
    border: 0,
    borderTop: "1px solid rgba(15, 23, 42, 0.10)",
  },
  h2: {
    marginTop: 18,
    marginBottom: 8,
    fontSize: 18,
  },
  h3: {
    marginTop: 12,
    marginBottom: 6,
    fontSize: 15,
    color: "#334155",
  },
  p: {
    margin: "8px 0",
    lineHeight: 1.6,
  },
  ul: {
    margin: "8px 0 0 18px",
    padding: 0,
    lineHeight: 1.6,
  },
  li: {
    margin: "6px 0",
  },
  note: {
    marginTop: 12,
    padding: 12,
    background: "#f8fafc",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 12,
    color: "#334155",
  },
  link: {
    color: "#2563eb",
    textDecoration: "underline",
  },
  footer: {
    marginTop: 22,
    paddingTop: 12,
    borderTop: "1px solid rgba(15, 23, 42, 0.10)",
    textAlign: "center",
  },
  small: { color: "#64748b" },
};
